import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DATA_ELEMENT_TYPES } from "@/utils/dataTypes";

/**
 * Reusable form fields for element version editing.
 * Used in both ElementVersionModal and dictionary import edit flow.
 *
 * @param {Object} props
 * @param {Object} props.formData - Form state object
 * @param {Function} props.onChange - (field, value) => void
 * @param {boolean} [props.disabled=false] - Disable all fields
 * @param {Array} [props.referenceLists=[]] - Available reference lists
 * @param {boolean} [props.showChangeDescription=true] - Show change description field
 * @param {boolean} [props.useReferenceListName=false] - Use list_name instead of id for reference list value
 */
export default function ElementVersionForm({
  formData,
  onChange,
  disabled = false,
  referenceLists = [],
  showChangeDescription = true,
  useReferenceListName = false
}) {
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="definition">Definition *</Label>
        <Textarea
          id="definition"
          value={formData.definition || ''}
          onChange={(e) => onChange('definition', e.target.value)}
          placeholder="Detailed business definition of the element..."
          rows={3}
          required
          disabled={disabled}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <div>
          <Label htmlFor="data_type">Data Type *</Label>
          <Select
            value={formData.data_type || 'string'}
            onValueChange={(value) => onChange('data_type', value)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DATA_ELEMENT_TYPES.map(t => (
                <SelectItem key={t} value={t}>{t}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="completeness_weight">Completeness Weight</Label>
          <Select value={String(formData.completeness_weight ?? 1)} onValueChange={(v) => onChange('completeness_weight', parseInt(v))} disabled={disabled}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 - Optional (Low)</SelectItem>
              <SelectItem value="1">1 - Standard (Medium)</SelectItem>
              <SelectItem value="2">2 - Critical (High)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">Field importance for completeness scoring</p>
        </div>

        <div>
          <Label htmlFor="timeliness_weight">Timeliness Weight</Label>
          <Select value={String(formData.timeliness_weight ?? 1)} onValueChange={(v) => onChange('timeliness_weight', parseInt(v))} disabled={disabled}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">0 - Static (Not Time-Sensitive)</SelectItem>
              <SelectItem value="1">1 - Moderate (Time-Sensitive)</SelectItem>
              <SelectItem value="2">2 - High (Highly Time-Sensitive)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500 mt-1">Time sensitivity for timeliness scoring</p>
        </div>

        <div>
          <Label htmlFor="status">Status</Label>
          <Select
            value={formData.status || 'DRAFT'}
            onValueChange={(value) => onChange('status', value)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="DRAFT">Draft</SelectItem>
              <SelectItem value="PENDING_REVIEW">Pending Review</SelectItem>
              <SelectItem value="APPROVED">Approved</SelectItem>
              <SelectItem value="DEPRECATED">Deprecated</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="confidence_score">Confidence Score</Label>
          <Input
            id="confidence_score"
            type="number"
            step="0.01"
            min="0"
            max="1"
            value={formData.confidence_score || 0}
            onChange={(e) => onChange('confidence_score', parseFloat(e.target.value))}
            placeholder="0.00 to 1.00"
            disabled={disabled}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="reference_list">Reference List</Label>
        {useReferenceListName ? (
          <Select
            value={formData.reference_list_name || '_none_'}
            onValueChange={(value) => onChange('reference_list_name', value === '_none_' ? null : value)}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select reference list (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="_none_">None</SelectItem>
              {referenceLists.map((list) => (
                <SelectItem key={list.id} value={list.list_name}>{list.list_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Select
            value={formData.reference_list_id}
            onValueChange={(value) => onChange('reference_list_id', value === 'none' ? null : Number(value))}
            disabled={disabled}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select reference list (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {referenceLists.map((list) => (
                <SelectItem key={list.id} value={list.id}>{list.list_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {showChangeDescription && (
        <div>
          <Label htmlFor="change_description">Change Description</Label>
          <Textarea
            id="change_description"
            value={formData.change_description || ''}
            onChange={(e) => onChange('change_description', e.target.value)}
            placeholder="Describe what changed in this version..."
            rows={2}
            disabled={disabled}
          />
        </div>
      )}
    </div>
  );
}
